import { IOrderTemplate, OrderTemplate } from '$models'
import { SharedSlice } from 'src/components/slice'
import { StateCreator } from 'zustand'
import { enqueueLoadedSnackbar, exceptionMessageHandler, logger } from '$utils'
import { orderTemplateService } from '$services'
import { PurchaseOrderUi } from '../purchase-orders/types'
import useStockStore from '../../store'
import { convertPurchaseOrderUiToOrderTemplate } from './helpers/helper'
import { orderBy } from 'lodash'
import dayjs from 'dayjs'

interface OrderTemplatesState {
  purchaseOrderUi?: PurchaseOrderUi
  orderTemplates: Record<string, IOrderTemplate>
  showDeactivated: boolean
}

export interface OrderTemplatesSlice extends OrderTemplatesState {
  getOrderTemplates: (includeDeletedTemplates?: boolean) => Promise<void>

  getOrderTemplate: (id: string) => Promise<IOrderTemplate | null>

  deactivateOrRestoreOrderTemplate: (
    orderTemplateId: string,
    action: 'deactivate' | 'restore',
    successMessage?: string,
    errorMessage?: string
  ) => Promise<IOrderTemplate | null>

  setShowDeactivated: () => void

  resetOrderTemplateSlice: () => void

  updateOrderTemplate: (newTemplateName?: string) => Promise<void>

  saveAsNewTemplate: (templateName: string) => Promise<boolean>
}

const initialOrderTemplatesState: OrderTemplatesState = {
  purchaseOrderUi: {
    lines: [],
    notes: undefined,
  },
  orderTemplates: {},
  showDeactivated: false,
}

export const createOrderTemplatesSlice: StateCreator<
  OrderTemplatesSlice & SharedSlice,
  [],
  [],
  OrderTemplatesSlice
> = (set, get) => ({
  ...initialOrderTemplatesState,

  resetOrderTemplateSlice: () => set(initialOrderTemplatesState),

  setShowDeactivated: () =>
    set((state) => ({ showDeactivated: !state.showDeactivated })),

  getOrderTemplates: async (
    includeDeletedTemplates: boolean = false
  ): Promise<void> => {
    set(() => ({ isLoading: true }))
    try {
      const orderTemplates = await orderTemplateService.getAll(
        includeDeletedTemplates
      )

      const orderedTemplates = orderBy(orderTemplates, ['createdAt'], ['asc'])

      // Convert array to record/object with id as key
      const templatesRecord = orderedTemplates.reduce((acc, template) => {
        if (template.id) {
          acc[template.id] = template
        }
        return acc
      }, {} as Record<string, IOrderTemplate>)

      set((state) => ({
        orderTemplates: includeDeletedTemplates
          ? { ...state.orderTemplates, ...templatesRecord }
          : templatesRecord,
      }))
    } catch (error) {
      exceptionMessageHandler(error, 'Failed to get order templates')
      if (!includeDeletedTemplates) {
        set(() => ({
          orderTemplates: {},
        }))
      }
    } finally {
      set(() => ({ isLoading: false }))
    }
  },

  getOrderTemplate: async (id: string): Promise<IOrderTemplate | null> => {
    set(() => ({ isLoading: true }))
    try {
      const orderTemplate = await orderTemplateService.get(id)

      return orderTemplate
    } catch (error) {
      exceptionMessageHandler(error, 'Failed to retrieve a order template')
      return null
    } finally {
      set(() => ({ isLoading: false }))
    }
  },

  deactivateOrRestoreOrderTemplate: async (
    orderTemplateId: string,
    action: 'deactivate' | 'restore',
    successMessage?: string,
    errorMessage?: string
  ): Promise<IOrderTemplate | null> => {
    const { orderTemplates } = get()
    const orderTemplate = orderTemplates[orderTemplateId]

    if (!orderTemplate) {
      logger.error(`${errorMessage} - orderTemplate doesn't exist`)
      enqueueLoadedSnackbar(
        errorMessage || 'Failed to process order template',
        'error'
      )
      return null
    }

    try {
      // We do an optimistic update of the orderTemplates state so it is reflected in the UI
      // and if it fails we revert the state back to the original state
      const updatedTemplate = {
        ...orderTemplate,
        deletedAt: action === 'deactivate' ? dayjs() : undefined,
      }

      set((state) => ({
        orderTemplates: {
          ...state.orderTemplates,
          [orderTemplateId]: updatedTemplate,
        },
      }))

      // Call the appropriate API method based on action
      if (action === 'deactivate') {
        await orderTemplateService.remove(orderTemplateId)
      } else {
        // For restore, we need to create an OrderTemplate instance for the API call
        const orderTemplateInstance = new OrderTemplate({
          ...updatedTemplate,
          deletedAt: undefined,
        })
        await orderTemplateService.update(
          orderTemplateId,
          orderTemplateInstance
        )
      }

      enqueueLoadedSnackbar(
        successMessage || `Order template ${action}d successfully`,
        'success'
      )
      return updatedTemplate
    } catch (error) {
      // Rollback the state
      set((state) => ({
        orderTemplates: {
          ...state.orderTemplates,
          [orderTemplateId]: orderTemplate, // Restore original template
        },
      }))
      exceptionMessageHandler(
        error,
        errorMessage || `Failed to ${action} order template`
      )
      return null
    }
  },

  updateOrderTemplate: async (newTemplateName?: string): Promise<void> => {
    const { purchaseOrderUi, orderTemplates, items, suppliers, brands, units } =
      useStockStore.getState()

    if (!purchaseOrderUi?.id || !purchaseOrderUi.lines) {
      logger.error('Cannot update template: missing purchaseOrderUi data')
      return
    }

    const purchaseOrderUiToUpdate = newTemplateName
      ? { ...purchaseOrderUi, templateName: newTemplateName }
      : purchaseOrderUi

    const updatedOrderTemplate = convertPurchaseOrderUiToOrderTemplate(
      purchaseOrderUiToUpdate,
      items,
      suppliers,
      brands,
      units
    )

    set(() => ({ isLoading: true }))
    try {
      await orderTemplateService.update(
        purchaseOrderUi.id,
        updatedOrderTemplate
      )

      // Update local state only after successful API call
      if (orderTemplates) {
        set((state) => ({
          orderTemplates: {
            ...state.orderTemplates,
            [purchaseOrderUi.id!]: updatedOrderTemplate,
          },
        }))
      }

      // Update purchaseOrderUi with new name if changed
      if (newTemplateName) {
        set(() => ({ purchaseOrderUi: purchaseOrderUiToUpdate }))
      }

      enqueueLoadedSnackbar('Order template updated successfully', 'success')
    } catch (error) {
      exceptionMessageHandler(error, 'Failed to update order template')
    } finally {
      set(() => ({ isLoading: false }))
    }
  },

  saveAsNewTemplate: async (templateName: string): Promise<boolean> => {
    const { purchaseOrderUi, items, suppliers, brands, units } =
      useStockStore.getState()

    if (!purchaseOrderUi?.lines || purchaseOrderUi.lines.length === 0) {
      logger.error('Cannot save template: no lines in purchaseOrderUi')
      return false
    }

    // Remove IDs from the template and lines since we're creating a new template
    const newPurchaseOrderUi = {
      ...purchaseOrderUi,
      id: undefined,
      templateName,
      lines: purchaseOrderUi.lines.map((line) => ({
        ...line,
        id: undefined,
      })),
    }

    const newOrderTemplate = convertPurchaseOrderUiToOrderTemplate(
      newPurchaseOrderUi,
      items,
      suppliers,
      brands,
      units
    )

    set(() => ({ isLoading: true }))
    try {
      const createdTemplate = await orderTemplateService.create(
        newOrderTemplate
      )

      if (createdTemplate?.id) {
        // Add to local orderTemplates state
        set((state) => ({
          orderTemplates: {
            ...state.orderTemplates,
            [createdTemplate.id!]: createdTemplate,
          },
        }))

        enqueueLoadedSnackbar(
          'New order template saved successfully',
          'success'
        )
        return true
      }
      return false
    } catch (error) {
      exceptionMessageHandler(error, 'Failed to save new order template')
      return false
    } finally {
      set(() => ({ isLoading: false }))
    }
  },
})

export default createOrderTemplatesSlice
